// =============================================================================
// RAPPORT DE CHANTIER - PDF de suivi en cours (2 pages)
// Photo instantanée du chantier EN COURS pour le suivi
// IMPORTANT: Tous les montants affichés sont en HT (Hors Taxes)
// =============================================================================

import {
  Document,
  Page,
  Text,
  View,
} from '@react-pdf/renderer';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { sharedStyles, COLORS, getMargeConfig } from './pdfStyles';
import {
  formatMontant,
  formatMontantHT,
  formatDate,
  formatPct,
  calculerJoursEcoules,
  calculerAvancement,
  calculerEcartJours,
  getStatutPlanning,
  calculerVentilation,
  genererAlertes,
  genererResume,
  type PDFData,
} from './pdfUtils';

// =============================================================================
// PROPS
// =============================================================================

interface RapportChantierPDFProps {
  data: PDFData;
  entrepriseNom?: string;
}

// =============================================================================
// COMPOSANT PRINCIPAL
// =============================================================================

export function RapportChantierPDF({ data, entrepriseNom = 'B8ild' }: RapportChantierPDFProps) {
  const { chantier, devis, depenses, equipe, metriques, suspensions } = data;

  // Calculs - utiliser la date de fin réelle si elle existe et est passée
  const aujourdhui = new Date();
  const dateFinReelle = chantier.date_fin_reelle ? new Date(chantier.date_fin_reelle) : null;
  const dateFinPrevue = chantier.date_fin_prevue ? new Date(chantier.date_fin_prevue) : null;

  // Si le chantier est terminé (date fin réelle renseignée et passée), utiliser la durée réelle
  const chantierTermine = dateFinReelle && dateFinReelle <= aujourdhui;

  // Durée réelle = jours-homme / nb membres OU durée calendaire
  const dureeReelleCalculee = equipe.membres.length > 0 && equipe.total_jours > 0
    ? Math.round((equipe.total_jours / equipe.membres.length) * 10) / 10
    : null;

  // Jours écoulés : si chantier terminé, prendre la durée réelle, sinon les jours depuis le début
  const joursEcoules = chantierTermine && dureeReelleCalculee
    ? dureeReelleCalculee
    : calculerJoursEcoules(chantier.date_debut);

  const avancement = calculerAvancement(joursEcoules, chantier.duree_prevue);
  const ecartJours = calculerEcartJours(joursEcoules, chantier.duree_prevue);
  const statutPlanning = getStatutPlanning(ecartJours);
  const margeConfig = getMargeConfig(metriques.marge_pct);
  const ventilation = calculerVentilation(depenses.total_mo, depenses.total_factures, depenses.total_frais);
  const alertes = genererAlertes(data);
  const resumeRAS = genererResume(alertes);
  const dateGeneration = format(new Date(), "d MMMM yyyy", { locale: fr });

  return (
    <Document>
      {/* ================================================================= */}
      {/* PAGE 1 : SYNTHÈSE                                                 */}
      {/* ================================================================= */}
      <Page size="A4" style={sharedStyles.page}>
        {/* Header */}
        <View style={sharedStyles.header}>
          <View style={sharedStyles.headerLeft}>
            <Text style={sharedStyles.logoText}>B<Text style={{ color: '#F59E0B' }}>8</Text>ild</Text>
          </View>
          <View style={sharedStyles.headerRight}>
            <Text style={sharedStyles.headerTitle}>RAPPORT DE SUIVI</Text>
            <Text style={sharedStyles.headerSubtitle}>{dateGeneration}</Text>
          </View>
        </View>
        <View style={sharedStyles.bandeDore} />

        <View style={sharedStyles.content}>
          {/* Encart infos chantier */}
          <View style={sharedStyles.chantierBox}>
            <Text style={sharedStyles.chantierNom}>{chantier.nom}</Text>
            <View style={sharedStyles.chantierInfoRow}>
              <Text style={sharedStyles.chantierLabel}>Client</Text>
              <Text style={sharedStyles.chantierValue}>{chantier.client || 'Non renseigné'}</Text>
            </View>
            <View style={sharedStyles.chantierInfoRow}>
              <Text style={sharedStyles.chantierLabel}>Adresse</Text>
              <Text style={sharedStyles.chantierValue}>{chantier.adresse || 'Non renseignée'}</Text>
            </View>
            {chantier.reference && (
              <View style={sharedStyles.chantierInfoRow}>
                <Text style={sharedStyles.chantierLabel}>Référence</Text>
                <Text style={sharedStyles.chantierValue}>{chantier.reference}</Text>
              </View>
            )}
          </View>

          {/* KPIs principaux : Avancement + Rentabilité */}
          <View style={sharedStyles.kpiRow}>
            {/* Avancement */}
            <View style={sharedStyles.kpiCard}>
              <Text style={sharedStyles.kpiTitle}>{chantierTermine ? 'Durée réelle' : 'Avancement'}</Text>
              <Text style={sharedStyles.kpiValue}>
                {chantierTermine ? `${joursEcoules} jours` : `Jour ${joursEcoules} / ${chantier.duree_prevue}`}
              </Text>
              <Text style={[sharedStyles.kpiSubvalue, { color: COLORS.grisTexte, fontSize: 10, marginTop: 4 }]}>
                {chantierTermine ? `Prévu : ${chantier.duree_prevue}j` : `${avancement}%`}
              </Text>
              <Text style={{ fontSize: 9, color: COLORS.grisTexte, marginTop: 6 }}>
                Début : {formatDate(chantier.date_debut)}
              </Text>
              {/* Si retard déclaré : afficher date initiale + date réelle (avec retard) */}
              {(metriques.jours_retard_travailles || 0) > 0 && chantier.date_fin_initiale ? (
                <>
                  <Text style={{ fontSize: 9, color: COLORS.grisTexte }}>
                    Fin initiale : {formatDate(chantier.date_fin_initiale)}
                  </Text>
                  <Text style={{ fontSize: 9, color: '#F59E0B', fontFamily: 'Helvetica-Bold' }}>
                    Fin réelle : {formatDate(chantier.date_fin_prevue)}
                  </Text>
                </>
              ) : (
                <Text style={{ fontSize: 9, color: COLORS.grisTexte }}>
                  {chantierTermine ? `Fin : ${formatDate(chantier.date_fin_reelle!)}` : `Fin prévue : ${formatDate(chantier.date_fin_prevue)}`}
                </Text>
              )}
            </View>

            {/* Rentabilité */}
            <View style={[sharedStyles.kpiCard, sharedStyles.kpiCardHighlight]}>
              <Text style={sharedStyles.kpiTitle}>Rentabilité</Text>
              <Text style={[sharedStyles.kpiValue, { color: margeConfig.color }]}>
                {formatPct(metriques.marge_pct)}
              </Text>
              <View style={[sharedStyles.kpiBadge, { backgroundColor: margeConfig.bgColor }]}>
                <Text style={[sharedStyles.kpiBadgeText, { color: margeConfig.color }]}>
                  {margeConfig.label}
                </Text>
              </View>
              <Text style={{ fontSize: 9, color: COLORS.grisTexte, marginTop: 8 }}>
                Budget dispo : {formatMontantHT(metriques.budget_disponible)}
              </Text>
            </View>
          </View>

          <View style={sharedStyles.separator} />

          {/* Tableau de bord financier */}
          <View style={sharedStyles.section}>
            <View style={sharedStyles.sectionHeader}>
              <Text style={sharedStyles.sectionTitle}>TABLEAU DE BORD FINANCIER</Text>
            </View>

            <View style={sharedStyles.statsRow}>
              <View style={sharedStyles.statsBox}>
                <Text style={[sharedStyles.statsValue, { fontSize: 16 }]}>{formatMontant(devis.montant_ht)}</Text>
                <Text style={{ fontSize: 9, color: COLORS.grisTexte, marginTop: 2 }}>HT</Text>
                <Text style={sharedStyles.statsLabel}>Devis</Text>
              </View>
              <View style={sharedStyles.statsBox}>
                <Text style={[sharedStyles.statsValue, { fontSize: 16, color: COLORS.rougeAlerte }]}>
                  {formatMontant(depenses.total)}
                </Text>
                <Text style={{ fontSize: 9, color: COLORS.grisTexte, marginTop: 2 }}>HT</Text>
                <Text style={sharedStyles.statsLabel}>Depense</Text>
              </View>
              <View style={sharedStyles.statsBox}>
                <Text style={[sharedStyles.statsValue, { fontSize: 16, color: metriques.marge_euros >= 0 ? COLORS.vertSucces : COLORS.rougeAlerte }]}>
                  {formatMontant(metriques.marge_euros)}
                </Text>
                <Text style={{ fontSize: 9, color: COLORS.grisTexte, marginTop: 2 }}>HT</Text>
                <Text style={sharedStyles.statsLabel}>Marge finale estimée</Text>
              </View>
            </View>

            {/* Détail des dépenses (HT) */}
            <View style={sharedStyles.table}>
              <View style={sharedStyles.tableHeader}>
                <Text style={[sharedStyles.tableHeaderCell, { flex: 2 }]}>Poste</Text>
                <Text style={[sharedStyles.tableHeaderCell, sharedStyles.tableCellRight]}>Montant HT</Text>
                <Text style={[sharedStyles.tableHeaderCell, sharedStyles.tableCellRight]}>Part</Text>
              </View>

              <View style={sharedStyles.tableRow}>
                <Text style={[sharedStyles.tableCell, { flex: 2 }]}>Matériaux (factures)</Text>
                <Text style={sharedStyles.tableCellRight}>{formatMontant(depenses.total_factures)}</Text>
                <Text style={sharedStyles.tableCellRight}>{ventilation.materiaux}%</Text>
              </View>

              <View style={[sharedStyles.tableRow, sharedStyles.tableRowAlt]}>
                <Text style={[sharedStyles.tableCell, { flex: 2 }]}>Frais annexes</Text>
                <Text style={sharedStyles.tableCellRight}>{formatMontant(depenses.total_frais)}</Text>
                <Text style={sharedStyles.tableCellRight}>{ventilation.frais}%</Text>
              </View>

              <View style={sharedStyles.tableRow}>
                <Text style={[sharedStyles.tableCell, { flex: 2 }]}>Main d'oeuvre (à date)</Text>
                <Text style={sharedStyles.tableCellRight}>{formatMontant(depenses.total_mo)}</Text>
                <Text style={sharedStyles.tableCellRight}>{ventilation.mo}%</Text>
              </View>

              <View style={[sharedStyles.tableRow, sharedStyles.tableRowTotal]}>
                <Text style={[sharedStyles.tableCell, sharedStyles.tableCellBold, { flex: 2 }]}>TOTAL DÉPENSÉ</Text>
                <Text style={[sharedStyles.tableCellRight, sharedStyles.tableCellBold]}>
                  {formatMontant(depenses.total)}
                </Text>
                <Text style={[sharedStyles.tableCellRight, sharedStyles.tableCellBold]}>100%</Text>
              </View>
            </View>
          </View>

          <View style={[sharedStyles.separator, { marginVertical: 10 }]} />

          {/* Alertes et points d'attention */}
          <View style={sharedStyles.section}>
            <View style={sharedStyles.sectionHeader}>
              <Text style={sharedStyles.sectionTitle}>ALERTES ET POINTS D'ATTENTION</Text>
            </View>

            {resumeRAS ? (
              <View style={[sharedStyles.alertBox, sharedStyles.alertSuccess]}>
                <Text style={sharedStyles.alertText}>{resumeRAS}</Text>
              </View>
            ) : (
              alertes.map((alerte, index) => (
                <View
                  key={index}
                  style={[
                    sharedStyles.alertBox,
                    alerte.includes('Budget')
                      ? sharedStyles.alertDanger
                      : sharedStyles.alertWarning,
                  ]}
                >
                  <Text style={sharedStyles.alertText}>{alerte}</Text>
                </View>
              ))
            )}
          </View>
        </View>

        {/* Footer - fixed pour éviter le débordement sur une nouvelle page */}
        <View style={sharedStyles.footer} fixed>
          <View style={sharedStyles.footerBande} />
          <View style={sharedStyles.footerContent}>
            <Text style={sharedStyles.footerText}>
              B8ild - {entrepriseNom} - Rapport généré le {dateGeneration}
            </Text>
            <Text style={sharedStyles.footerPage}>Page 1/2</Text>
          </View>
        </View>
      </Page>

      {/* ================================================================= */}
      {/* PAGE 2 : DÉTAILS                                                  */}
      {/* ================================================================= */}
      <Page size="A4" style={sharedStyles.page}>
        {/* Header */}
        <View style={sharedStyles.header}>
          <View style={sharedStyles.headerLeft}>
            <Text style={sharedStyles.logoText}>B<Text style={{ color: '#F59E0B' }}>8</Text>ild</Text>
          </View>
          <View style={sharedStyles.headerRight}>
            <Text style={sharedStyles.headerTitle}>DÉTAILS</Text>
            <Text style={sharedStyles.headerSubtitle}>{chantier.nom}</Text>
          </View>
        </View>
        <View style={sharedStyles.bandeDore} />

        <View style={sharedStyles.content}>
          {/* Équipe affectée */}
          <View style={sharedStyles.section}>
            <View style={sharedStyles.sectionHeader}>
              <Text style={sharedStyles.sectionTitle}>ÉQUIPE AFFECTÉE</Text>
            </View>

            <View style={sharedStyles.table}>
              <View style={sharedStyles.tableHeader}>
                <Text style={[sharedStyles.tableHeaderCell, { flex: 2 }]}>Nom</Text>
                <Text style={[sharedStyles.tableHeaderCell, { flex: 1.5 }]}>Poste</Text>
                <Text style={[sharedStyles.tableHeaderCell, sharedStyles.tableCellRight]}>Jours</Text>
                <Text style={[sharedStyles.tableHeaderCell, sharedStyles.tableCellRight]}>Coût</Text>
              </View>

              {equipe.membres.length > 0 ? (
                <>
                  {equipe.membres.map((membre, index) => (
                    <View
                      key={index}
                      style={[sharedStyles.tableRow, index % 2 === 1 ? sharedStyles.tableRowAlt : {}]}
                    >
                      <Text style={[sharedStyles.tableCell, { flex: 2 }]}>{membre.nom}</Text>
                      <Text style={[sharedStyles.tableCell, { flex: 1.5 }]}>{membre.poste}</Text>
                      <Text style={sharedStyles.tableCellRight}>{membre.jours}j</Text>
                      <Text style={sharedStyles.tableCellRight}>{formatMontant(membre.cout)}</Text>
                    </View>
                  ))}
                  <View style={[sharedStyles.tableRow, sharedStyles.tableRowTotal]}>
                    <Text style={[sharedStyles.tableCell, sharedStyles.tableCellBold, { flex: 2 }]}>TOTAL</Text>
                    <Text style={[sharedStyles.tableCell, { flex: 1.5 }]}>{equipe.membres.length} pers.</Text>
                    <Text style={[sharedStyles.tableCellRight, sharedStyles.tableCellBold]}>
                      {equipe.total_jours}j
                    </Text>
                    <Text style={[sharedStyles.tableCellRight, sharedStyles.tableCellBold]}>
                      {formatMontant(equipe.total_cout)}
                    </Text>
                  </View>
                </>
              ) : (
                <View style={sharedStyles.tableRow}>
                  <Text style={[sharedStyles.tableCell, { flex: 1, textAlign: 'center', color: COLORS.grisTexte }]}>
                    Aucun membre affecté pour le moment
                  </Text>
                </View>
              )}
            </View>
          </View>

          <View style={[sharedStyles.separator, { marginVertical: 10 }]} />

          {/* Achats et Frais */}
          <View style={sharedStyles.section}>
            <View style={sharedStyles.sectionHeader}>
              <Text style={sharedStyles.sectionTitle}>ACHATS ET FRAIS</Text>
            </View>

            {/* Factures fournisseurs */}
            {depenses.factures.length > 0 && (
              <>
                <Text style={{ fontSize: 10, fontFamily: 'Helvetica-Bold', color: COLORS.bleuRoi, marginBottom: 8 }}>
                  FACTURES FOURNISSEURS ({depenses.factures.length})
                </Text>
                <View style={sharedStyles.table}>
                  <View style={sharedStyles.tableHeader}>
                    <Text style={[sharedStyles.tableHeaderCell, { flex: 2 }]}>Fournisseur</Text>
                    <Text style={[sharedStyles.tableHeaderCell, { flex: 1.5 }]}>Catégorie</Text>
                    <Text style={[sharedStyles.tableHeaderCell, sharedStyles.tableCellRight]}>Montant HT</Text>
                  </View>

                  {depenses.factures.slice(0, 5).map((facture, index) => (
                    <View
                      key={index}
                      style={[sharedStyles.tableRow, index % 2 === 1 ? sharedStyles.tableRowAlt : {}]}
                    >
                      <Text style={[sharedStyles.tableCell, { flex: 2 }]}>{facture.fournisseur}</Text>
                      <Text style={[sharedStyles.tableCell, { flex: 1.5 }]}>{facture.categorie}</Text>
                      <Text style={sharedStyles.tableCellRight}>{formatMontant(facture.montant_ht)}</Text>
                    </View>
                  ))}

                  <View style={[sharedStyles.tableRow, sharedStyles.tableRowTotal]}>
                    <Text style={[sharedStyles.tableCell, sharedStyles.tableCellBold, { flex: 3.5 }]}>
                      TOTAL FACTURES
                    </Text>
                    <Text style={[sharedStyles.tableCellRight, sharedStyles.tableCellBold]}>
                      {formatMontant(depenses.total_factures)}
                    </Text>
                  </View>
                </View>
              </>
            )}

            {/* Frais annexes */}
            {depenses.frais.length > 0 && (
              <>
                <Text style={{ fontSize: 10, fontFamily: 'Helvetica-Bold', color: COLORS.bleuRoi, marginBottom: 8, marginTop: 8 }}>
                  FRAIS ANNEXES ({depenses.frais.length})
                </Text>
                <View style={sharedStyles.table}>
                  <View style={sharedStyles.tableHeader}>
                    <Text style={[sharedStyles.tableHeaderCell, { flex: 1.5 }]}>Type</Text>
                    <Text style={[sharedStyles.tableHeaderCell, { flex: 2 }]}>Description</Text>
                    <Text style={[sharedStyles.tableHeaderCell, sharedStyles.tableCellRight]}>Montant</Text>
                  </View>

                  {depenses.frais.slice(0, 5).map((frais, index) => (
                    <View
                      key={index}
                      style={[sharedStyles.tableRow, index % 2 === 1 ? sharedStyles.tableRowAlt : {}]}
                    >
                      <Text style={[sharedStyles.tableCell, { flex: 1.5 }]}>{frais.type}</Text>
                      <Text style={[sharedStyles.tableCell, { flex: 2 }]}>{frais.description || '-'}</Text>
                      <Text style={sharedStyles.tableCellRight}>{formatMontant(frais.montant)}</Text>
                    </View>
                  ))}

                  <View style={[sharedStyles.tableRow, sharedStyles.tableRowTotal]}>
                    <Text style={[sharedStyles.tableCell, sharedStyles.tableCellBold, { flex: 3.5 }]}>
                      TOTAL FRAIS
                    </Text>
                    <Text style={[sharedStyles.tableCellRight, sharedStyles.tableCellBold]}>
                      {formatMontant(depenses.total_frais)}
                    </Text>
                  </View>
                </View>
              </>
            )}

            {depenses.factures.length === 0 && depenses.frais.length === 0 && (
              <View style={[sharedStyles.alertBox, { backgroundColor: COLORS.grisClair, borderLeftColor: COLORS.grisTexte }]}>
                <Text style={sharedStyles.alertText}>Aucune facture ou frais enregistré pour le moment</Text>
              </View>
            )}
          </View>

          <View style={sharedStyles.separator} />

          {/* Projection fin de chantier - wrap={false} pour éviter la coupure de page */}
          <View style={sharedStyles.section} wrap={false}>
            <View style={sharedStyles.sectionHeader}>
              <Text style={sharedStyles.sectionTitle}>PROJECTION FIN DE CHANTIER</Text>
            </View>

            <View style={sharedStyles.projectionBox}>
              <Text style={sharedStyles.projectionTitle}>Si le rythme actuel se maintient :</Text>

              <View style={sharedStyles.projectionItem}>
                <Text style={sharedStyles.projectionBullet}>-</Text>
                <Text style={sharedStyles.projectionText}>
                  Marge finale estimee : {formatMontantHT(metriques.marge_euros)} ({formatPct(metriques.marge_pct)})
                </Text>
              </View>

              <View style={sharedStyles.projectionItem}>
                <Text style={sharedStyles.projectionBullet}>-</Text>
                <Text style={sharedStyles.projectionText}>
                  Date de fin estimee : {formatDate(chantier.date_fin_prevue)}{(metriques.jours_retard_travailles || 0) > 0 ? ` (+${metriques.jours_retard_travailles}j retard)` : ''}{(metriques.jours_reprise_travailles || 0) > 0 ? ` (+${metriques.jours_reprise_travailles}j reprise)` : ''} ({statutPlanning.label})
                </Text>
              </View>

              <View style={sharedStyles.projectionItem}>
                <Text style={sharedStyles.projectionBullet}>-</Text>
                <Text style={sharedStyles.projectionText}>
                  Statut : {margeConfig.label}
                </Text>
              </View>

              {metriques.cout_journalier > 0 && (
                <View style={sharedStyles.projectionItem}>
                  <Text style={sharedStyles.projectionBullet}>-</Text>
                  <Text style={sharedStyles.projectionText}>
                    Cout journalier moyen : {formatMontantHT(metriques.cout_journalier)}/jour
                  </Text>
                </View>
              )}

              {suspensions && suspensions.total_jours > 0 && (
                <View style={sharedStyles.projectionItem}>
                  <Text style={sharedStyles.projectionBullet}>-</Text>
                  <Text style={sharedStyles.projectionText}>
                    Suspensions : {suspensions.nb_suspensions} suspension{suspensions.nb_suspensions > 1 ? 's' : ''} ({suspensions.total_jours}j, cout mobilisation {formatMontantHT(suspensions.total_cout)})
                  </Text>
                </View>
              )}
            </View>
          </View>
        </View>

        {/* Footer - fixed pour éviter le débordement sur une nouvelle page */}
        <View style={sharedStyles.footer} fixed>
          <View style={sharedStyles.footerBande} />
          <View style={sharedStyles.footerContent}>
            <Text style={sharedStyles.footerText}>
              B8ild - {entrepriseNom} - Rapport généré le {dateGeneration}
            </Text>
            <Text style={sharedStyles.footerPage}>Page 2/2</Text>
          </View>
        </View>
      </Page>
    </Document>
  );
}

export default RapportChantierPDF;
